<template>
  <modal :name="modalName" :adaptive="true" :scrollable="true" height="auto" :width="420" class="chat-modal">
    <!-- ヘッダー -->
    <div class="chat-header">
      <div class="chat-header-content">
        <div class="chat-avatar">
          <img src="/static/img/yagichat/yagish_icon.png" alt="ヤギコンシェルジュ" />
        </div>
        <div class="chat-header-text">
          <div class="chat-title">{{ headerTitle }}</div>
          <div class="chat-subtitle">{{ headerSubtitle }}</div>
        </div>
      </div>
      <button @click="closeDialog()" class="close-btn">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <!-- チャット形式UI -->
    <div v-if="!showResult" class="chat-container">

      <!-- チャットメッセージ -->
      <div class="chat-messages" ref="chatMessages">

        <!-- 日付表示 -->
        <div class="chat-date">{{ getCurrentDate() }}</div>

        <div v-for="(message, index) in chatMessages" :key="index" :class="['chat-message',
          message.type === 'question' ? 'chat-question' :
            message.type === 'answer' ? 'chat-answer' : 'chat-system']">
          <div v-if="message.type === 'question'" class="question-bubble">
            <div class="question-text">{{ message.text }}</div>
            <div v-if="message.description" class="question-description">{{ message.description }}</div>
          </div>
          <div v-else-if="message.type === 'answer'" class="answer-bubble">
            <div class="answer-text">{{ message.text }}</div>
          </div>
          <div v-else-if="message.type === 'system'" class="system-bubble">
            <div class="system-content">
              <div class="system-icon">✓</div>
              <div class="system-text-wrapper">
                <div class="system-text">{{ message.text }}</div>
                <div v-if="message.skippedQuestions && message.skippedQuestions.length > 0" class="skipped-list">
                  <div v-for="(q, i) in message.skippedQuestions" :key="i" class="skipped-item">
                    ・{{ q }}
                  </div>
                </div>
                <div v-if="message.skippedIndices && message.skippedIndices.length > 0"
                  class="edit-skipped-btn-wrapper">
                  <el-button size="mini" @click="editSkippedQuestions(message.skippedIndices)">編集する</el-button>
                </div>
              </div>
            </div>
          </div>
          <!-- AI 生成結果の確認メッセージ（質問バブル風 + 結果テキスト + 3 ボタン） -->
          <!-- active=false の古い候補はボタン disabled で閲覧のみ可能 -->
          <div v-else-if="message.type === 'ai-result'" class="question-bubble ai-result-bubble"
            :class="{ 'ai-result-bubble--inactive': !message.active }">
            <div class="question-text">文章は下記でいかがですか？</div>
            <div class="ai-result-content">{{ message.text }}</div>
            <div class="ai-result-actions">
              <button class="ai-result-button ai-result-button--accept"
                :disabled="!message.active" @click="handleAiResultAccept">はい</button>
              <button class="ai-result-button ai-result-button--regenerate"
                :disabled="!message.active" @click="handleAiResultRegenerate">再生成する</button>
              <button class="ai-result-button ai-result-button--edit"
                :disabled="!message.active" @click="handleAiResultEdit">自分で直す</button>
            </div>
          </div>
          <!-- 企業情報取得成功時のシステムメッセージ -->
          <div v-else-if="message.type === 'company-info'" class="system-bubble company-info-bubble">
            <div class="system-content">
              <div class="system-icon">✓</div>
              <div class="system-text-wrapper">
                <div class="system-text">{{ message.text }}</div>
                <div class="company-info-list">
                  <div v-if="message.companyData.company_name" class="company-info-item">
                    ・会社名: {{ message.companyData.company_name }}
                  </div>
                  <div v-if="message.companyData.industry" class="company-info-item">
                    ・業種: {{ message.companyData.industry }}
                  </div>
                  <div v-if="message.companyData.job" class="company-info-item">
                    ・職種: {{ message.companyData.job }}
                  </div>
                  <div v-if="message.companyData.values" class="company-info-item">
                    ・特徴: {{ message.companyData.values }}
                  </div>
                </div>
                <div v-if="message.editableFields && message.editableFields.length > 0"
                  class="edit-skipped-btn-wrapper">
                  <el-button size="mini" @click="editCompanyInfo(message.editableFields)">編集する</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 考え中インジケータ（AI 生成中などに表示） -->
        <div v-if="thinkingMode" class="chat-message chat-system">
          <div class="thinking-bubble">
            <i class="el-icon-loading thinking-icon"></i>
            <span class="thinking-text">{{ thinkingLabel }}</span>
          </div>
        </div>
      </div>

      <!-- 入力エリアまたはAI生成ボタン -->
      <!-- AI 結果確認モード or 考え中モード中は入力エリア・完了ボタン両方を隠す
           （チャット内のボタンで進行する / 考え中の間は止まったように見せない） -->
      <div class="chat-input-area" v-show="!aiResultMode && !thinkingMode">
        <!-- ローディング中 -->
        <template v-if="isLoadingCompanyInfo">
          <div class="loading-container">
            <i class="el-icon-loading"></i>
            <span>企業情報を取得中...</span>
          </div>
        </template>

        <!-- 質問が残っている場合：入力フィールド -->
        <template v-else-if="currentQuestionIndex < questions.length && !allQuestionsCompleted">
          <!-- URL質問タイプの場合 -->
          <template v-if="currentQuestion && currentQuestion.type === 'url'">
            <div class="quick-reply-container">
              <button class="quick-reply-button" @click="skipUrlQuestion">スキップする</button>
            </div>
            <div class="input-container">
              <input type="url" :placeholder="currentQuestion.placeholder || 'URLを入力してください'"
                v-model="currentAnswer" @keydown.enter="handleEnterKey" @compositionstart="isComposing = true"
                @compositionend="isComposing = false" class="chat-input-field">
              <button class="send-button" @click="submitUrlAnswer">
                <img src="/static/img/yagichat/send-icon.svg" alt="">
              </button>
            </div>
            <div class="progress-info">
              <span class="progress-text">質問 {{ currentQuestionIndex + 1 }} / {{ questions.length }}</span>
            </div>
          </template>

          <!-- 選択肢ボタン型質問（quickReplies 付き） -->
          <template v-else-if="currentQuestion && currentQuestion.quickReplies && currentQuestion.quickReplies.length > 0">
            <div class="quick-reply-container quick-reply-choices">
              <button
                v-for="(reply, idx) in currentQuestion.quickReplies"
                :key="idx"
                class="quick-reply-button"
                @click="submitQuickReply(reply)">
                {{ reply.label }}
              </button>
            </div>
            <div class="progress-info">
              <span class="progress-text">質問 {{ currentQuestionIndex + 1 }} / {{ questions.length }}</span>
            </div>
          </template>

          <!-- 通常の質問タイプ -->
          <template v-else>
            <div v-if="questions[currentQuestionIndex].allowNone" class="quick-reply-container">
              <button class="quick-reply-button" @click="submitNoneAnswer">なし</button>
            </div>
            <div class="input-container">
              <input type="text" :placeholder="questions[currentQuestionIndex].placeholder || '回答を入力してください'"
                v-model="currentAnswer" @keydown.enter="handleEnterKey" @compositionstart="isComposing = true"
                @compositionend="isComposing = false" class="chat-input-field">
              <button class="send-button" @click="submitAnswer">
                <img src="/static/img/yagichat/send-icon.svg" alt="">
              </button>
            </div>
            <div class="progress-info">
              <span class="progress-text">質問 {{ currentQuestionIndex + 1 }} / {{ questions.length }}</span>
              <span v-if="showUsageCount && usageCount !== null" class="usage-text">
                {{ usageCountLabel }}: {{ usageCount }} / {{ maxUsageCount }}回
              </span>
            </div>
          </template>
        </template>

        <!-- 全ての質問回答完了 or 全て入力済みの場合：AI生成ボタン -->
        <template v-else>
          <div class="all-filled-container">
            <div class="all-filled-message">
              <i class="el-icon-success"></i>
              <span>全ての情報が入力されています</span>
            </div>
            <el-button type="success" @click="handleQuestionsComplete" class="generate-button">
              {{ completeButtonText }}
            </el-button>
          </div>
        </template>
      </div>
    </div>

    <!-- 結果表示エリア（スロットで外部から制御） -->
    <div v-if="showResult" class="result-container">
      <slot name="result" :result="result" :close="closeDialog"></slot>
    </div>
  </modal>
</template>

<script>
export default {
  name: 'YagiChatBot',
  props: {
    // モーダル名（一意である必要がある）
    modalName: {
      type: String,
      required: true
    },
    // 質問リスト
    questions: {
      type: Array,
      required: true,
      // 各質問の形式: { id, question, description, placeholder, field }
      validator: (questions) => {
        return questions.every(q => q.id && q.question && q.field)
      }
    },
    // データオブジェクト（v-model的に使用）
    value: {
      type: Object,
      required: true
    },
    // 自動スキップを有効にするか
    autoSkipFilled: {
      type: Boolean,
      default: true
    },
    // 利用回数を表示するか
    showUsageCount: {
      type: Boolean,
      default: false
    },
    // 利用回数
    usageCount: {
      type: Number,
      default: null
    },
    // 最大利用回数
    maxUsageCount: {
      type: Number,
      default: 1
    },
    // 利用回数のラベル
    usageCountLabel: {
      type: String,
      default: '利用回数'
    },
    // GA4イベント用のチャット種別
    chatType: {
      type: String,
      default: 'unknown'
    },
    // 全質問完了時に表示するボタンのテキスト
    completeButtonText: {
      type: String,
      default: 'AI文章作成をする'
    },
    // ヘッダーに表示するタイトル
    headerTitle: {
      type: String,
      default: 'ヤギコンシェルジュ'
    },
    // ヘッダーに表示するサブタイトル
    headerSubtitle: {
      type: String,
      default: 'ヤギッシュ公式チャットボットです'
    },
    // チャット開始時に最初に表示する挨拶メッセージ（空文字なら非表示）
    welcomeMessage: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      currentQuestionIndex: 0,
      chatMessages: [],
      currentAnswer: '',
      isComposing: false,
      showResult: false,
      result: null,
      isLoadingCompanyInfo: false,
      autoFilledFields: [], // URLから自動入力されたフィールドを追跡
      allQuestionsCompleted: false, // 全質問回答完了フラグ
      aiResultMode: false, // AI 生成結果の確認メッセージ表示中フラグ（入力エリアを隠す）
      aiResultCallbacks: null, // AI 結果確認時の選択肢コールバック（onAccept/onRegenerate/onEdit）
      thinkingMode: false, // AI 考え中インジケータ表示フラグ（入力エリアを隠して「考え中…」表示）
      thinkingLabel: '考え中…' // 考え中表示のラベル文言
    }
  },
  computed: {
    currentQuestion () {
      if (this.currentQuestionIndex < this.questions.length) {
        return this.questions[this.currentQuestionIndex]
      }
      return null
    }
  },
  methods: {
    // モーダルを開く
    open () {
      this.resetChat()
      this.$modal.show(this.modalName)

      // GA4イベント送信（チャット開始）
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'yagi_chat_open', {
          chat_type: this.chatType,
          total_questions: this.questions.length
        })
      }

      if (this.autoSkipFilled) {
        this.skipFilledQuestions()
      } else {
        this.addQuestionToChat(0)
      }
    },
    // モーダルを閉じる
    // options.suppressCloseEvent: true のとき、GA4 close イベントと $emit('close') を抑制する
    // （完了フロー経由で呼ぶときに、未完了扱いの計測や onClose ハンドラ二重実行を防ぐ）
    closeDialog (options) {
      const suppressCloseEvent = !!(options && options.suppressCloseEvent)

      // GA4イベント送信（チャット閉じる - 完了前に閉じた場合）
      if (typeof window.gtag === 'function' && !this.showResult && !suppressCloseEvent) {
        window.gtag('event', 'yagi_chat_close', {
          chat_type: this.chatType,
          completed: false,
          answered_questions: this.currentQuestionIndex
        })
      }

      this.$modal.hide(this.modalName)
      this.showResult = false

      if (!suppressCloseEvent) {
        this.$emit('close')
      }
    },
    // チャットをリセット
    resetChat () {
      this.currentQuestionIndex = 0
      this.chatMessages = []
      this.currentAnswer = ''
      this.showResult = false
      this.result = null
      this.autoFilledFields = []
      this.allQuestionsCompleted = false
      this.aiResultMode = false
      this.aiResultCallbacks = null
      this.thinkingMode = false
      this.thinkingLabel = '考え中…'

      if (this.welcomeMessage) {
        this.chatMessages.push({
          type: 'question',
          text: this.welcomeMessage
        })
      }
    },
    // 既に入力済みの質問をスキップ
    skipFilledQuestions () {
      let skippedCount = 0
      const skippedQuestions = []
      const skippedIndices = []

      while (this.currentQuestionIndex < this.questions.length) {
        const question = this.questions[this.currentQuestionIndex]

        // skipIf を満たす質問は静かにスキップ（ユーザーの入力ではないので記録しない）
        if (question.skipIf && question.skipIf(this.value)) {
          this.currentQuestionIndex++
          continue
        }

        const value = this.value[question.field]
        if (value && value.trim && value.trim()) {
          // 既に埋まっている質問はスキップ + 記録
          const displayText = question.question.replace(/を教えてください$/, '')
          skippedQuestions.push(displayText)
          skippedIndices.push(this.currentQuestionIndex)
          this.currentQuestionIndex++
          skippedCount++
        } else {
          break
        }
      }

      if (skippedCount > 0) {
        this.chatMessages.push({
          type: 'system',
          text: `履歴書から${skippedCount}件の情報を取得しました`,
          skippedQuestions,
          skippedIndices
        })
      }

      if (this.currentQuestionIndex < this.questions.length) {
        this.addQuestionToChat(this.currentQuestionIndex)
        const currentQuestion = this.questions[this.currentQuestionIndex]
        this.currentAnswer = this.value[currentQuestion.field] || ''
      }

      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // 質問をチャットに追加
    addQuestionToChat (index) {
      if (index >= this.questions.length) {
        return
      }

      const question = this.questions[index]
      this.chatMessages.push({
        type: 'question',
        text: question.question,
        description: question.description,
        placeholder: question.placeholder,
        questionId: question.id
      })

      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // 回答を送信
    submitAnswer () {
      const currentQuestion = this.questions[this.currentQuestionIndex]

      // 回答を保存（v-model 更新用のマージ値を作成して親に emit）
      const updatedValue = {
        ...this.value,
        [currentQuestion.field]: this.currentAnswer
      }
      this.$emit('input', updatedValue)

      // 回答をチャットに追加
      if (this.currentAnswer && this.currentAnswer.trim()) {
        this.chatMessages.push({
          type: 'answer',
          text: this.currentAnswer
        })
      }

      // 次の質問へ
      this.currentQuestionIndex++
      this.currentAnswer = ''

      // 親への $emit は非同期なので this.value はまだ古い。最新値を引数で渡す
      this.moveToNextQuestion(updatedValue)
    },
    // 「なし」ボタンで回答を送信
    submitNoneAnswer () {
      const currentQuestion = this.questions[this.currentQuestionIndex]

      // 「なし」として保存
      const updatedValue = {
        ...this.value,
        [currentQuestion.field]: 'なし'
      }
      this.$emit('input', updatedValue)

      // 回答をチャットに追加
      this.chatMessages.push({
        type: 'answer',
        text: 'なし'
      })

      // 次の質問へ
      this.currentQuestionIndex++
      this.currentAnswer = ''

      this.moveToNextQuestion(updatedValue)
    },
    // 選択肢ボタン（quickReplies）からの回答送信
    submitQuickReply (reply) {
      const currentQuestion = this.questions[this.currentQuestionIndex]

      // reply.value を保存、reply.label を answer バブルに表示
      const updatedValue = {
        ...this.value,
        [currentQuestion.field]: reply.value
      }
      this.$emit('input', updatedValue)

      this.chatMessages.push({
        type: 'answer',
        text: reply.label
      })

      this.currentQuestionIndex++
      this.currentAnswer = ''

      // skipIf で直前の回答値を参照するため、最新値を引数で渡す
      this.moveToNextQuestion(updatedValue)
    },
    // 次の質問に進む共通ヘルパー
    // latestValue: 直前に更新した回答値（未指定なら this.value を使う）
    // skipIf を満たす質問を連続スキップし、autoFilledFields も考慮する
    moveToNextQuestion (latestValue) {
      // $emit('input', ...) は非同期で親の値を更新するため、
      // 直前の回答を反映した値で skipIf を評価する必要がある
      const valueForSkip = latestValue || this.value

      // skipIf を満たす質問を連続スキップ
      while (
        this.currentQuestionIndex < this.questions.length &&
        this.questions[this.currentQuestionIndex].skipIf &&
        this.questions[this.currentQuestionIndex].skipIf(valueForSkip)
      ) {
        this.currentQuestionIndex++
      }

      // 自動入力済みフィールドがある場合はスキップ処理
      if (this.autoFilledFields.length > 0) {
        this.moveToNextUnskippedQuestion(this.autoFilledFields)
      } else if (this.currentQuestionIndex < this.questions.length) {
        this.addQuestionToChat(this.currentQuestionIndex)
        const nextQuestion = this.questions[this.currentQuestionIndex]
        // quickReplies 質問は currentAnswer をリセット、通常質問は既存値を prefill
        this.currentAnswer = (nextQuestion && !nextQuestion.quickReplies)
          ? (valueForSkip[nextQuestion.field] || '')
          : ''
      } else {
        // 全ての質問が終了 → complete イベントを即発火
        // （親コンポーネントが AI 生成等の後続処理を即座に開始できるようにする。
        //   親がハンドリングしない場合は allQuestionsCompleted で完了 UI を表示する）
        this.$emit('complete')
        // complete ハンドラで thinkingMode 等が設定されなかった場合のフォールバック
        if (!this.thinkingMode) {
          this.allQuestionsCompleted = true
        }
      }

      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // スキップした質問を編集
    editSkippedQuestions (skippedIndices) {
      if (skippedIndices && skippedIndices.length > 0) {
        // 編集モードではスキップ処理を無効化
        this.autoFilledFields = []
        // AI 結果確認モードを抜ける（入力エリアを表示できるようにする）
        this.aiResultMode = false
        this.aiResultCallbacks = null

        this.currentAnswer = ''
        this.currentQuestionIndex = skippedIndices[0]
        this.chatMessages = []
        this.addQuestionToChat(this.currentQuestionIndex)

        const currentQuestion = this.questions[this.currentQuestionIndex]
        this.currentAnswer = this.value[currentQuestion.field] || ''
      }
    },
    // Enterキーハンドリング
    handleEnterKey (event) {
      if (this.isComposing) {
        return
      }
      event.preventDefault()
      // URL質問の場合は専用メソッドを呼び出す
      if (this.currentQuestion && this.currentQuestion.type === 'url') {
        this.submitUrlAnswer()
      } else {
        this.submitAnswer()
      }
    },
    // 現在の日付を取得
    getCurrentDate () {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${year}.${month}.${day}`
    },
    // 結果を表示
    showResultView (resultData) {
      this.result = resultData
      this.showResult = true
    },
    // AI 考え中インジケータを表示する（外部から呼ぶ）
    // 入力エリア・完了ボタンを隠して「考え中…」バブルを末尾に表示
    showThinkingIndicator (label) {
      this.thinkingMode = true
      this.thinkingLabel = label || '考え中…'
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // AI 考え中インジケータを非表示にする（外部から呼ぶ）
    hideThinkingIndicator () {
      this.thinkingMode = false
    },
    // AI 生成結果を確認メッセージとしてチャット内に追加する
    // text: 生成された文章
    // callbacks: { onAccept, onRegenerate, onEdit }
    showAiResultConfirm (text, callbacks) {
      // 既存の ai-result メッセージを inactive に（古い候補のボタンを disabled にする）
      this.chatMessages.forEach(function (m) {
        if (m.type === 'ai-result') m.active = false
      })
      this.aiResultCallbacks = callbacks || null
      this.aiResultMode = true
      this.chatMessages.push({
        type: 'ai-result',
        text,
        active: true
      })
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // 「はい」ボタン押下: 結果を確定
    handleAiResultAccept () {
      // ユーザー回答バブルとして痕跡を残す
      this.chatMessages.push({ type: 'answer', text: 'はい' })
      const cb = this.aiResultCallbacks
      this.aiResultMode = false
      this.aiResultCallbacks = null
      if (cb && typeof cb.onAccept === 'function') {
        cb.onAccept()
      }
    },
    // 「再生成する」ボタン押下: 再度 AI 生成を呼び出す
    // 古い候補メッセージは削除せず、inactive 状態で残す（再生成失敗時のフォールバック）
    // aiResultMode は true のまま維持（再生成完了まで入力エリアを隠して全画面ローディングに任せる）
    handleAiResultRegenerate () {
      this.chatMessages.push({ type: 'answer', text: '再生成する' })
      const cb = this.aiResultCallbacks
      // 再生成中の見た目: aiResultMode を true のまま、fullscreenLoading で全画面表示
      if (cb && typeof cb.onRegenerate === 'function') {
        cb.onRegenerate()
      }
    },
    // 特定の質問 ID から質問入力を再開する（外部から呼ぶ）
    // AI 結果確認モードを抜けて、指定した質問をチャットに追加して入力欄を表示する
    // chatMessages はそのまま残し、新しい質問を末尾に追加する形で会話を継続する
    resumeFromQuestionId (questionId) {
      let index = -1
      for (let i = 0; i < this.questions.length; i++) {
        if (this.questions[i].id === questionId) {
          index = i
          break
        }
      }
      if (index === -1) return
      // 各種モードをリセットして通常の質問入力フローに戻す
      this.aiResultMode = false
      this.aiResultCallbacks = null
      this.thinkingMode = false
      this.allQuestionsCompleted = false
      this.currentQuestionIndex = index
      this.currentAnswer = this.value[this.questions[index].field] || ''
      this.addQuestionToChat(index)
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // 「自分で直す」ボタン押下: 編集画面で手動修正へ
    handleAiResultEdit () {
      this.chatMessages.push({ type: 'answer', text: '自分で直す' })
      const cb = this.aiResultCallbacks
      this.aiResultMode = false
      this.aiResultCallbacks = null
      if (cb && typeof cb.onEdit === 'function') {
        cb.onEdit()
      }
    },
    // 全ての質問回答完了 or 全て入力済みの場合の完了処理
    handleQuestionsComplete () {
      // GA4イベント送信（チャット完了）
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'yagi_chat_complete', {
          chat_type: this.chatType,
          total_questions: this.questions.length,
          used_url_autofill: this.autoFilledFields.length > 0,
          autofilled_fields: this.autoFilledFields.length
        })
      }

      this.$emit('complete', this.value)
    },
    // URL質問をスキップ
    skipUrlQuestion () {
      // 回答をチャットに追加（スキップした旨）
      this.chatMessages.push({
        type: 'answer',
        text: 'スキップしました'
      })

      // GA4イベント送信（スキップ）
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'ai_company_url_extract', {
          success: false,
          error: 'skipped'
        })
      }

      // 次の質問へ
      this.currentQuestionIndex++
      this.currentAnswer = ''

      if (this.currentQuestionIndex < this.questions.length) {
        this.addQuestionToChat(this.currentQuestionIndex)
        const nextQuestion = this.questions[this.currentQuestionIndex]
        this.currentAnswer = this.value[nextQuestion.field] || ''
      }

      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // URL回答を送信して企業情報を取得
    async submitUrlAnswer () {
      const currentQuestion = this.questions[this.currentQuestionIndex]
      const url = this.currentAnswer.trim()

      // URLが空の場合はスキップ
      if (!url) {
        this.skipUrlQuestion()
        return
      }

      // URLをチャットに追加
      this.chatMessages.push({
        type: 'answer',
        text: url
      })

      // URLを保存
      this.$emit('input', {
        ...this.value,
        [currentQuestion.field]: url
      })

      // ローディング開始
      this.isLoadingCompanyInfo = true

      try {
        // 企業情報を取得するAPIを呼び出し
        const response = await this.$axios.post(this.$varuna.endPoints.aiExtractCompanyInfo, { url })

        if (response.data.success && response.data.data) {
          const companyData = response.data.data
          const autoFillFields = currentQuestion.autoFillFields || {}

          // 自動入力を行う
          const updatedValue = { ...this.value }
          const filledFields = []

          if (companyData.company_name && autoFillFields.company_name) {
            updatedValue[autoFillFields.company_name] = companyData.company_name
            filledFields.push(autoFillFields.company_name)
          }
          if (companyData.industry && autoFillFields.industry) {
            updatedValue[autoFillFields.industry] = companyData.industry
            filledFields.push(autoFillFields.industry)
          }
          if (companyData.job && autoFillFields.job) {
            updatedValue[autoFillFields.job] = companyData.job
            filledFields.push(autoFillFields.job)
          }
          if (companyData.values && autoFillFields.values) {
            updatedValue[autoFillFields.values] = companyData.values
            filledFields.push(autoFillFields.values)
          }

          // 値を更新
          this.$emit('input', updatedValue)

          // 自動入力されたフィールドを保存（後続の質問スキップ用）
          this.autoFilledFields = filledFields

          // システムメッセージを表示
          this.chatMessages.push({
            type: 'company-info',
            text: '企業情報を取得しました',
            companyData,
            editableFields: filledFields
          })

          // GA4イベント送信（成功）
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'ai_company_url_extract', {
              success: true,
              fields_filled: filledFields.length,
              has_company_name: !!companyData.company_name,
              has_industry: !!companyData.industry,
              has_job: !!companyData.job,
              has_values: !!companyData.values
            })
          }

          // 次の質問へ進む前に、自動入力された質問をスキップする処理
          this.currentQuestionIndex++
          this.currentAnswer = ''
          this.skipAutoFilledQuestions(filledFields)
        } else {
          // GA4イベント送信（失敗）
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'ai_company_url_extract', {
              success: false,
              error: response.data.error || 'unknown_error'
            })
          }

          // 失敗時はエラーメッセージを表示して次へ
          this.chatMessages.push({
            type: 'system',
            text: response.data.error || '企業情報を取得できませんでした。手動で入力してください。'
          })
          this.currentQuestionIndex++
          this.currentAnswer = ''
          if (this.currentQuestionIndex < this.questions.length) {
            this.addQuestionToChat(this.currentQuestionIndex)
          }
        }
      } catch (error) {
        console.error('企業情報取得エラー:', error)

        // GA4イベント送信（例外エラー）
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'ai_company_url_extract', {
            success: false,
            error: 'exception'
          })
        }

        // エラー時は通常フローへ
        this.chatMessages.push({
          type: 'system',
          text: '企業情報の取得に失敗しました。手動で入力してください。'
        })
        this.currentQuestionIndex++
        this.currentAnswer = ''
        if (this.currentQuestionIndex < this.questions.length) {
          this.addQuestionToChat(this.currentQuestionIndex)
        }
      } finally {
        this.isLoadingCompanyInfo = false
        this.$nextTick(() => {
          if (this.$refs.chatMessages) {
            this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
          }
        })
      }
    },
    // 自動入力されたフィールドに対応する質問をスキップ
    skipAutoFilledQuestions (filledFields) {
      // 次のスキップしない質問を探す
      this.moveToNextUnskippedQuestion(filledFields)

      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        }
      })
    },
    // 次のスキップしない質問に移動
    moveToNextUnskippedQuestion (filledFields) {
      while (this.currentQuestionIndex < this.questions.length) {
        const question = this.questions[this.currentQuestionIndex]
        // autoSkipIfFilledがtrueで、かつフィールドが自動入力されている場合はスキップ
        if (question.autoSkipIfFilled && filledFields.includes(question.field)) {
          this.currentQuestionIndex++
        } else {
          // スキップしない質問に到達
          break
        }
      }

      // 次の質問があれば表示
      if (this.currentQuestionIndex < this.questions.length) {
        this.addQuestionToChat(this.currentQuestionIndex)
        const nextQuestion = this.questions[this.currentQuestionIndex]
        this.currentAnswer = this.value[nextQuestion.field] || ''
      } else {
        // 全ての質問が終了 → complete イベントを即発火
        this.$emit('complete')
        if (!this.thinkingMode) {
          this.allQuestionsCompleted = true
        }
      }
    },
    // 企業情報を編集（自動入力された項目を手動で編集）
    editCompanyInfo (editableFields) {
      // 編集モードではスキップ処理を無効化
      this.autoFilledFields = []
      // AI 結果確認モードを抜ける（入力エリアを表示できるようにする）
      this.aiResultMode = false
      this.aiResultCallbacks = null

      // 編集可能なフィールドに対応する最初の質問を探す
      for (let i = 0; i < this.questions.length; i++) {
        if (editableFields.includes(this.questions[i].field)) {
          this.currentQuestionIndex = i
          this.chatMessages = []
          this.addQuestionToChat(i)
          const currentQuestion = this.questions[i]
          this.currentAnswer = this.value[currentQuestion.field] || ''
          break
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

// チャットモーダルのスタイル
.chat-modal {
  ::v-deep .v--modal-box {
    border-radius: 12px;
    overflow: hidden;
    padding: 0 !important;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

    @include max-width(sm) {
      width: 95% !important;
      margin: 0 auto;
    }
  }
}

// ヘッダー
.chat-header {
  background: #004D40;
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .chat-header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .chat-avatar {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .chat-header-text {
    .chat-title {
      font-weight: bold;
      font-size: 1rem;
      margin-bottom: 2px;
    }

    .chat-subtitle {
      font-size: 0.75rem;
      opacity: 0.9;
    }
  }

  .close-btn {
    background: transparent;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }

    i {
      font-size: 1.5rem;
    }
  }
}

// 日付表示
.chat-date {
  text-align: center;
  color: #999;
  font-size: 0.75rem;
  padding: 10px 0;
  background: #ffffff;
}

// チャットコンテナ
.chat-container {
  height: 550px;
  display: flex;
  flex-direction: column;
}

// 結果表示コンテナ（チャットと同じ高さ）
.result-container {
  height: 550px;
  overflow-y: auto;
  padding: 20px;
  background: #ffffff;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #27ae60;
    border-radius: 10px;

    &:hover {
      background: #229954;
    }
  }
}

// チャットメッセージエリア
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  padding-top: 0;
  background: #ffffff;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

.chat-message {
  margin-bottom: 15px;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-question {
  display: flex;
  justify-content: flex-start;
}

.question-bubble {
  background: #eef5e8;
  padding: 12px 16px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  max-width: 85%;

  .question-text {
    font-weight: 500;
    margin-bottom: 4px;
    line-height: 1.5;
  }

  .question-description {
    font-size: 0.85rem;
    color: #666;
    margin-top: 6px;
    line-height: 1.4;
  }
}

// 考え中インジケータ（AI 生成中の視覚フィードバック）
.thinking-bubble {
  background: #eef5e8;
  padding: 12px 16px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 85%;

  .thinking-icon {
    color: #0F9934;
    font-size: 1.2rem;
  }

  .thinking-text {
    color: #2d6b3a;
    font-size: 0.92rem;
    font-weight: 500;
  }
}

// AI 生成結果の確認バブル（質問バブル風 + 結果テキスト + 3 ボタン）
.ai-result-bubble {
  max-width: 95%;

  // 古い候補（inactive）は薄く表示してボタン操作不可
  &--inactive {
    opacity: 0.55;
  }

  .ai-result-content {
    background: #fff;
    border: 1px solid #d6e3cf;
    border-radius: 8px;
    padding: 10px 12px;
    margin: 8px 0;
    line-height: 1.6;
    font-size: 0.92rem;
    color: #333;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .ai-result-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .ai-result-button {
    flex: 0 0 auto;
    background: #fff;
    border: 1px solid #0F9934;
    border-radius: 18px;
    padding: 8px 16px;
    font-size: 0.85rem;
    color: #0F9934;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: #0F9934;
      color: #fff;
    }

    &:active:not(:disabled) {
      transform: scale(0.95);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &.ai-result-button--accept {
      background: #0F9934;
      color: #fff;

      &:hover:not(:disabled) {
        background: #0c7a29;
      }
    }
  }
}

.chat-answer {
  display: flex;
  justify-content: flex-end;
}

.answer-bubble {
  background: #0F9934;
  padding: 12px 16px;
  border-radius: 12px;
  border-bottom-right-radius: 4px;
  max-width: 85%;
  border: 1px solid #0F9934;
  color: #ffffff;
  font-weight: bold;

  .answer-text {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
  }
}

.system-bubble {
  background: #fff3cd;
  color: #856404;
  padding: 12px 16px;
  border-radius: 12px;
  max-width: 90%;
  border: 1px solid #ffeaa7;

  .system-content {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .system-icon {
    font-size: 1.2rem;
    flex-shrink: 0;
    color: #27ae60;
    line-height: 1.4;
  }

  .system-text {
    font-weight: bold;
    font-size: 0.9rem;
  }

  .skipped-list {
    margin-top: 6px;
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: normal;

    .skipped-item {
      padding: 2px 0;
    }
  }

  .edit-skipped-btn-wrapper {
    margin-top: 10px;
    text-align: center;

    .el-button {
      background-color: #0F9934;
      border-color: #0F9934;
      color: white;

      &:hover {
        background-color: #ffffff;
        border-color: #0F9934;
        color: #0F9934;
        opacity: 1;
      }
    }
  }

  // 企業情報取得用のリスト
  .company-info-list {
    margin-top: 8px;
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: normal;

    .company-info-item {
      padding: 3px 0;
      line-height: 1.4;
    }
  }
}

// 企業情報取得成功時のバブル（緑色に）
.company-info-bubble {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;

  .system-text {
    color: #155724;
  }
}

.chat-input-area {
  background: white;
  padding: 15px 20px;

  .input-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .chat-input-field {
    flex: 1;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 10px;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #2d8b6f;
    }
  }

  .send-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      transform: scale(1.1);
      opacity: 1;
    }

  }

  .quick-reply-container {
    display: flex;
    justify-content: center;
    padding: 8px 0;
    gap: 8px;

    // 複数の選択肢ボタンが並ぶ型 (quickReplies 質問用)
    &.quick-reply-choices {
      flex-wrap: wrap;
      padding: 12px;
    }

    .quick-reply-button {
      background-color: #fff;
      border: 1px solid #0F9934;
      border-radius: 18px;
      padding: 8px 16px;
      font-size: 0.85rem;
      color: #0F9934;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        background-color: #0F9934;
        color: #fff;
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  .progress-info {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 0.75rem;
    color: #999;

    .progress-text {
      font-weight: 500;
    }

    .usage-text {
      color: #666;
    }
  }

  .all-filled-container {
    text-align: center;
    padding: 10px 0;

    .all-filled-message {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: 15px;
      color: #27ae60;
      font-weight: 500;
      font-size: 0.9rem;

      i {
        font-size: 1.2rem;
      }
    }

    .generate-button {
      width: 100%;
      font-size: 1rem;
      padding: 12px 0;
      font-weight: bold;
    }
  }

  // ローディング表示
  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 20px 0;
    color: #27ae60;
    font-size: 0.9rem;

    i {
      font-size: 1.5rem;
    }
  }
}
</style>
